import React, { useState, useRef, useLayoutEffect, useEffect, useMemo } from 'react';
import { useReducedMotion } from 'framer-motion';

interface MethodSwapOptions {
  /** Track container resizes between transitions (e.g. expandable content). */
  observeResize?: boolean;
}

/**
 * Handles smooth height transitions when swapping between method panels.
 *
 * The animation sequence:
 *   1. Freeze container at fromHeight (transition: none — prevents the browser
 *      from animating the height change caused by the DOM swap)
 *   2. rAF-poll the entering panel until its height stabilises
 *   3. Set targetHeight WITH CSS transition → browser animates fromHeight → target
 *   4. Re-measure after transition; smooth any discrepancy vs auto
 *   5. Reset to height:auto
 */
export function useMethodSwapAnimation(
  methodType: string | null | undefined,
  options?: MethodSwapOptions,
) {
  const prefersReducedMotion = useReducedMotion();
  const fadeDuration = prefersReducedMotion ? 0 : 0.2;
  const heightDuration = prefersReducedMotion ? 0 : 250;
  const [contentHeight, setContentHeight] = useState<number | 'auto'>('auto');
  const [skipTransition, setSkipTransition] = useState(false);
  const contentHeightRef = useRef<number | 'auto'>('auto');
  const containerRef = useRef<HTMLDivElement>(null);
  const activePanelRef = useRef<HTMLDivElement>(null);
  const previousTypeRef = useRef(methodType);
  const previousHeightRef = useRef<number | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateHeight = (value: number | 'auto') => {
    contentHeightRef.current = value;
    setContentHeight(value);
  };

  useLayoutEffect(() => {
    const methodChanged = previousTypeRef.current !== methodType;
    if (!methodChanged) {
      return;
    }

    const activePanel = activePanelRef.current;
    if (!activePanel) {
      previousTypeRef.current = methodType;
      return;
    }

    // Use getBoundingClientRect (not scrollHeight) to measure the container.
    // During mode="sync" crossfades, the exiting panel is position:absolute with
    // inset:0 — its content overflows its constrained box, and scrollHeight
    // includes that overflow, returning the OLD height instead of the new one.
    // getBoundingClientRect returns the actual rendered box, which is determined
    // only by the in-flow (entering) panel.
    const containerNaturalHeight = containerRef.current?.getBoundingClientRect().height
      ?? activePanel.getBoundingClientRect().height;
    const nextMeasuredHeight = Math.round(containerNaturalHeight);

    const currentContainerHeight = containerRef.current?.getBoundingClientRect().height;
    const currentHeight = contentHeightRef.current;
    const fromHeight = currentHeight === 'auto'
      ? (previousHeightRef.current ?? nextMeasuredHeight)
      : (currentContainerHeight ?? previousHeightRef.current ?? nextMeasuredHeight);

    previousTypeRef.current = methodType;
    previousHeightRef.current = nextMeasuredHeight;

    if (prefersReducedMotion || Math.abs(fromHeight - nextMeasuredHeight) < 1) {
      if (currentHeight !== 'auto') {
        updateHeight('auto');
      }
      return;
    }

    if (animFrameRef.current != null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }

    // Step 1: Freeze at fromHeight WITHOUT a CSS transition.
    // Both state updates batch into one React render:
    //   height: fromHeight, transition: none → container snaps instantly.
    setSkipTransition(true);
    updateHeight(fromHeight);

    // Step 2: On the next frame (after freeze is painted), transition to target.
    // We use nextMeasuredHeight (captured at auto BEFORE freezing) rather than
    // re-measuring the panel via rAF. Once frozen, the fixed-height container
    // constrains the CSS grid, which clamps the panel's getBoundingClientRect
    // to the frozen height — giving the wrong target.
    animFrameRef.current = requestAnimationFrame(() => {
      previousHeightRef.current = nextMeasuredHeight;
      setSkipTransition(false);
      updateHeight(nextMeasuredHeight);
      animFrameRef.current = null;

      // Step 3: Reset to auto after the transition completes.
      resetTimeoutRef.current = setTimeout(() => {
        updateHeight('auto');
        resetTimeoutRef.current = null;
      }, heightDuration + 40);
    });
  }, [methodType, prefersReducedMotion, heightDuration]);

  useEffect(() => {
    return () => {
      if (animFrameRef.current != null) {
        cancelAnimationFrame(animFrameRef.current);
      }
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  // Prime previousHeightRef once after mount. Ongoing CSS-only size changes are
  // tracked by ResizeObserver below.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const frame = requestAnimationFrame(() => {
      const h = Math.round(container.getBoundingClientRect().height);
      if (h > 0) previousHeightRef.current = h;
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      if (contentHeightRef.current === 'auto') {
        const h = Math.round(container.getBoundingClientRect().height);
        if (h > 0) previousHeightRef.current = h;
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const containerStyle: React.CSSProperties = useMemo(() => ({
    height: contentHeight === 'auto' ? 'auto' : `${contentHeight}px`,
    transition: contentHeight === 'auto' || prefersReducedMotion
      ? undefined
      : skipTransition
        ? 'none'
        : `height ${heightDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
  }), [contentHeight, skipTransition, heightDuration, prefersReducedMotion]);

  return {
    containerStyle,
    containerRef,
    activePanelRef,
    fadeDuration,
  };
}
